import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Alert, Typography } from "@mui/material";
import { MfaVerificationForm } from "../../components/auth";
import { useAuth } from "../../hooks";
import AuthLayout from "../../layouts/AuthLayout";

/**
 * Simplified MFA verification page
 *
 * Direct access to URL parameters without state management
 */

const MfaVerificationPage = () => {
  const { isAuthenticated, mfaEmail, requiresMfa, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get email from multiple sources - auth context, URL parameter, or state
  const emailFromUrl = searchParams.get("email");
  const emailFromState = location.state?.email;
  const email = mfaEmail || emailFromUrl || emailFromState || "";
  const from = location.state?.from?.pathname || "/";

  // Handle authentication state changes after MFA verification
  React.useEffect(() => {
    if (isAuthenticated) {
      console.log(
        "MFA verified, checking forcePasswordChange:",
        user?.forcePasswordChange,
      );

      // If user needs to reset password, redirect to forced reset page
      if (user?.forcePasswordChange) {
        navigate("/auth/forced-password-reset", { replace: true });
      } else {
        // Otherwise redirect to intended destination
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate, from]);

  // If no MFA is required and no email, redirect back to login
  if (!requiresMfa && !email) {
    navigate("/auth/login", { replace: true });
    return null;
  }

  return (
    <AuthLayout
      title="MFA Verification"
      alert={
        !email && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Email address is missing. Please try logging in again.
          </Alert>
        )
      }
    >
      {/* Debug information */}
      {process.env.NODE_ENV !== "production" && (
        <Box sx={{ mb: 2, p: 1, bgcolor: "#f5f5f5", fontSize: "0.75rem" }}>
          <div>Email from URL: {emailFromUrl || "none"}</div>
          <div>Email from state: {emailFromState || "none"}</div>
          <div>Email being used: {email || "none"}</div>
        </Box>
      )}

      {/* Only show form if we have an email */}
      {email ? (
        <MfaVerificationForm initialEmail={email} />
      ) : (
        <Box sx={{ mt: 3 }}>
          <Typography align="center">
            Please return to the login page and try again.
          </Typography>
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <a href="/auth/login">Return to Login</a>
          </Box>
        </Box>
      )}
    </AuthLayout>
  );
};

export default MfaVerificationPage;
